import type { Audience, BrandProfile, Pillar } from './brand';
import type { Channel } from './channels';

/**
 * Weekly planner: turns the brand's cadence and pillar weights into dated slots. Deterministic
 * (same brand + week = same plan), so re-planning a week never shuffles what people already saw.
 * The writer (LLM) fills each slot afterwards; human-only pillars get a reminder slot instead.
 */

export type PlanSlot = {
  channel: Channel;
  pillarId: string;
  audienceId: string | null;
  /** UTC instant the post goes out. */
  scheduledAt: Date;
  humanOnly: boolean;
  /** 0-based order within the week, stable across re-plans. */
  index: number;
};

/** Offset (ms) of `timeZone` from UTC at `instant`. */
function tzOffsetMs(instant: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
  return asUtc - Math.floor(instant.getTime() / 1000) * 1000;
}

/** The UTC instant of local wall time `yyyy-mm-dd HH:MM` in `timeZone` (DST-aware). */
export function zonedToUtc(date: string, time: string, timeZone: string): Date {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const [hh, mm] = time.split(':').map(Number) as [number, number];
  const guess = Date.UTC(y, m - 1, d, hh, mm);
  let ts = guess - tzOffsetMs(new Date(guess), timeZone);
  // A second pass settles instants next to a DST change.
  ts = guess - tzOffsetMs(new Date(ts), timeZone);
  return new Date(ts);
}

/** Local calendar date (yyyy-mm-dd) of `instant` in `timeZone`. */
export function localDate(instant: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(instant);
}

function addDays(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/** ISO weekday (1 = Monday) of a yyyy-mm-dd date. */
function isoWeekday(date: string): number {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return ((new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7) + 1;
}

/** Monday (yyyy-mm-dd, local to `timeZone`) of the week containing `instant`. */
export function weekStartOf(instant: Date, timeZone: string): string {
  const today = localDate(instant, timeZone);
  return addDays(today, 1 - isoWeekday(today));
}

/**
 * Smooth weighted round-robin: over N picks each pillar appears in proportion to its weight and
 * as evenly spread as possible (nginx's algorithm). Pillars with weight 0 never appear.
 */
export function distribute<T extends { weight: number }>(items: readonly T[], n: number): T[] {
  const live = items.filter((i) => i.weight > 0);
  if (!live.length || n <= 0) return [];
  const total = live.reduce((s, i) => s + i.weight, 0);
  const current = live.map(() => 0);
  const out: T[] = [];
  for (let k = 0; k < n; k++) {
    let best = 0;
    for (let i = 0; i < live.length; i++) {
      current[i]! += live[i]!.weight;
      if (current[i]! > current[best]!) best = i;
    }
    current[best]! -= total;
    out.push(live[best]!);
  }
  return out;
}

function pickAudience(audiences: readonly Audience[], pillarIndex: number, occurrence: number): string | null {
  if (!audiences.length) return null;
  return audiences[(pillarIndex + occurrence) % audiences.length]!.id;
}

/**
 * Slots for the week starting `weekStart` (a Monday, local). Posting times rotate through each
 * channel's preferred times; days spread evenly over the allowed days. Slots already in the past
 * (relative to `now`) are dropped.
 */
export function planWeek(brand: Pick<BrandProfile, 'timezone' | 'pillars' | 'audiences' | 'cadence'>, weekStart: string, now: Date = new Date()): PlanSlot[] {
  const raw: Omit<PlanSlot, 'pillarId' | 'audienceId' | 'humanOnly' | 'index'>[] = [];
  for (const c of brand.cadence) {
    const days = [...new Set(c.days)].filter((d) => d >= 1 && d <= 7).sort((a, b) => a - b);
    const times = c.times.length ? c.times : ['09:30'];
    const n = Math.max(0, Math.min(Math.floor(c.postsPerWeek), 21));
    if (!days.length || !n) continue;
    const perDay = new Map<number, number>();
    for (let k = 0; k < n; k++) {
      // Spread posts over the allowed days (3 posts on Mon-Fri -> Mon, Tue, Thu); a second post on
      // the same day takes the next preferred time.
      const day = days[Math.floor((k * days.length) / n)]!;
      const nth = perDay.get(day) ?? 0;
      perDay.set(day, nth + 1);
      const time = times[nth % times.length]!;
      raw.push({ channel: c.channel, scheduledAt: zonedToUtc(addDays(weekStart, day - 1), time, brand.timezone) });
    }
  }
  raw.sort((a, b) => a.scheduledAt.getTime() - b.scheduledAt.getTime() || a.channel.localeCompare(b.channel));
  const pillars = distribute<Pillar>(brand.pillars, raw.length);
  const seen = new Map<string, number>();
  return raw
    .map((slot, index) => {
      const pillar = pillars[index];
      if (!pillar) return null;
      const occurrence = seen.get(pillar.id) ?? 0;
      seen.set(pillar.id, occurrence + 1);
      const pillarIndex = brand.pillars.findIndex((p) => p.id === pillar.id);
      return { ...slot, pillarId: pillar.id, audienceId: pickAudience(brand.audiences, pillarIndex, occurrence), humanOnly: pillar.humanOnly, index };
    })
    .filter((s): s is PlanSlot => !!s && s.scheduledAt.getTime() > now.getTime());
}
